// /////////////////////////////////////////////////////////////
// [ファイル] AnkenNumberPicker.tsx
// [概要] 案件番号（Hozenall NET が採番し工事管理へ引き継ぐ 12 桁の統一コード）の入力部品。
//        ミラー（Cloud SQL の v_legacy_anken）を番号か案件名で検索して選び、番号の一覧（string[]）として保持する。
//        ミラーに無い 12 桁の番号を打つと、バックエンドがオンプレへ 1 件照会して補う（採番直後の案件向け）。
//        候補に無い番号は登録できない（A 厳格、2026-11-04 ユーザー決定）。工事管理システムに無い番号・失注／却下の案件・
//        他の案件に登録済みの番号は候補に出ないか選べず、バックエンドでも拒否する。
//          AnkenNumberPicker … ダイアログ・登録画面用（Autocomplete）
//          InlineAnkenField  … 詳細画面のインライン編集用（InlineEdit に載せる）
//          AnkenChips        … 表示用のチップ（番号は 2-4-6 桁、下に案件名）
// /////////////////////////////////////////////////////////////
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';
import { InlineEdit } from '../InlineEditField';
import {
  LegacyAnkenRow, SfaAnkenLink, sfaStructureApi, formatSystemNumber, normalizeSystemNumber, normalizeSystemNumbers,
  SYSTEM_NUMBER_HELP, SYSTEM_NUMBER_LABEL,
} from '../../hooks/useSfaStructure';

// 番号ごとの表示名（案件名・建物名・取引先名）。紐付け（anken_links）と検索で選んだ候補から集める
export interface AnkenInfo { anken_name?: string | null; building_name?: string | null; client_name?: string | null; deleted?: boolean; in_mirror?: boolean; status_name?: string | null; closed?: boolean }
type InfoMap = Record<string, AnkenInfo>;

const infoFromLinks = (links?: SfaAnkenLink[] | null): InfoMap => {
  const m: InfoMap = {};
  for (const l of links || []) m[l.anken_no] = { anken_name: l.anken_name, building_name: l.building_name, client_name: l.client_name, deleted: l.deleted, in_mirror: l.in_mirror, status_name: l.status_name, closed: l.closed };
  return m;
};
const infoFromRow = (r: LegacyAnkenRow): AnkenInfo => ({ anken_name: r.anken_name, building_name: r.building_name, client_name: r.client_name, deleted: r.deleted, in_mirror: true, status_name: r.status_name, closed: !r.selectable });
// チップの下に添える 1 行（案件名、無ければ建物名、無ければ取引先名）
const infoLine = (i?: AnkenInfo): string => (i ? (i.anken_name || i.building_name || i.client_name || '') : '');

// --------------------------------------------------
// 表示用チップ
// --------------------------------------------------
export function AnkenChips({ numbers, links, info, dense }: { numbers: string[] | null | undefined; links?: SfaAnkenLink[] | null; info?: InfoMap; dense?: boolean }) {
  const map = useMemo(() => ({ ...infoFromLinks(links), ...(info || {}) }), [links, info]);
  if (!numbers?.length) return <Typography component="span" variant="body2" color="text.disabled">-</Typography>;
  return (
    <Box component="span" sx={{ display: 'inline-flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {numbers.map((n) => {
        const i = map[n];
        const line = infoLine(i);
        return (
          <Box key={n} component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
            {/* 紐付けた後に元システムで失注・却下になった番号は、チップを赤枠にしてステータスを添える (紐付けは残す) */}
            <Tooltip title={[line, i?.closed ? `元システムで ${i.status_name || '失注・却下'}` : '', i?.in_mirror === false ? '手入力（ミラー未反映）' : ''].filter(Boolean).join('　')}>
              <Chip size="small" label={formatSystemNumber(n)} variant={i?.closed ? 'outlined' : 'filled'} color={i?.closed ? 'error' : 'default'}
                sx={{ height: dense ? 18 : 20, fontSize: dense ? '0.7rem' : '0.75rem', textDecoration: i?.deleted ? 'line-through' : undefined }} />
            </Tooltip>
            {!dense && (line || i?.closed) && (
              <Typography variant="caption" color={i?.closed ? 'error' : 'text.secondary'} sx={{ lineHeight: 1.2, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {i?.closed ? `${i.status_name || '失注・却下'}　` : ''}{line}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// --------------------------------------------------
// 検索して選ぶ入力（複数可）
// --------------------------------------------------
export function AnkenNumberPicker({ value, onChange, links, label, helperText, sx, autoFocus, disabled, onEscape, size = 'small' }: {
  value: string[];
  onChange: (numbers: string[], info: InfoMap) => void;
  links?: SfaAnkenLink[] | null; // 既存の紐付け（表示名の初期値）
  label?: string;
  helperText?: string;
  sx?: any;
  autoFocus?: boolean;
  disabled?: boolean;
  onEscape?: (e: React.KeyboardEvent) => void;
  size?: 'small' | 'medium';
}) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<LegacyAnkenRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [info, setInfo] = useState<InfoMap>(() => infoFromLinks(links));
  const seq = useRef(0);

  useEffect(() => { setInfo((prev) => ({ ...infoFromLinks(links), ...prev })); }, [links]);

  // 入力が止まってから検索する（番号は 2 桁から前方一致、名前は 2 文字から部分一致）
  useEffect(() => {
    const q = input.trim();
    if (q.length < 2) { setOptions([]); setNote(null); return; }
    const id = ++seq.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await sfaStructureApi.searchLegacyAnken(q, 20);
        if (id !== seq.current) return;
        setOptions(r.rows);
        if (r.message) setNote(r.message);
        else if (r.looked_up && r.exact) setNote('工事管理システムから取り込みました');
        else if (r.rows.length === 0) setNote('該当する案件がありません');
        else setNote(null);
      } catch (e: any) {
        if (id === seq.current) { setOptions([]); setNote(e?.message || '検索に失敗しました'); }
      } finally {
        if (id === seq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [input]);

  const commit = (vals: (string | LegacyAnkenRow)[]) => {
    const next: string[] = [];
    const add: InfoMap = {};
    for (const v of vals) {
      if (typeof v === 'string') { const n = normalizeSystemNumber(v); if (n) next.push(n); }
      else { next.push(v.anken_no); add[v.anken_no] = infoFromRow(v); }
    }
    const merged = { ...info, ...add };
    setInfo(merged);
    onChange(normalizeSystemNumbers(next), merged);
  };

  return (
    <Box sx={sx}>
      <Autocomplete<LegacyAnkenRow | string, true, false, false>
        multiple autoHighlight disableClearable={false} size={size} disabled={disabled}
        options={options} loading={loading} filterOptions={(x) => x}
        getOptionDisabled={(o) => typeof o !== 'string' && !!o.linked_project_id}
        noOptionsText={input.trim().length < 2 ? '番号か案件名を 2 文字以上' : (loading ? '検索中…' : '該当する案件がありません')}
        value={value}
        inputValue={input} onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); }}
        onChange={(_, vals) => commit(vals as (string | LegacyAnkenRow)[])}
        isOptionEqualToValue={(o, v) => (typeof o === 'string' ? o : o.anken_no) === (typeof v === 'string' ? v : v.anken_no)}
        getOptionLabel={(o) => (typeof o === 'string' ? formatSystemNumber(o) : o.anken_no_display)}
        getOptionKey={(o) => (typeof o === 'string' ? o : o.anken_no)}
        renderOption={(props, o) => {
          if (typeof o === 'string') return <li {...props} key={o}>{formatSystemNumber(o)}</li>;
          const { key, ...rest } = props as any;
          return (
            <li key={o.anken_no} {...rest}>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', textDecoration: o.deleted ? 'line-through' : undefined }}>
                  {o.anken_no_display}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {o.in_koji ? '工事管理' : ''}{o.in_koji && o.in_hozenallnet ? '・' : ''}{o.in_hozenallnet ? 'Hozenall NET' : ''}{o.status_name ? `　${o.status_name}` : ''}
                  </Typography>
                  {o.linked_project_id && <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>登録済み：{o.linked_project_name}</Typography>}
                </Typography>
                <Typography variant="body2" noWrap>{o.anken_name || o.building_name || '（案件名なし）'}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {[o.anken_name && o.building_name && o.anken_name !== o.building_name ? o.building_name : null, o.client_name, o.reception_date ? `受付 ${o.reception_date}` : null].filter(Boolean).join('　')}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderTags={(vals, getTagProps) => vals.map((o, i) => {
          const { key, ...p } = getTagProps({ index: i });
          const n = typeof o === 'string' ? o : o.anken_no;
          const line = infoLine(info[n]);
          return <Tooltip key={key} title={line}><Chip size="small" label={formatSystemNumber(n)} {...p} /></Tooltip>;
        })}
        renderInput={(params) => (
          <TextField
            {...params} label={label ?? SYSTEM_NUMBER_LABEL} autoFocus={autoFocus} placeholder="番号か案件名で検索"
            helperText={note || helperText || SYSTEM_NUMBER_HELP}
            onKeyDown={(e) => { if (e.key === 'Escape' && onEscape) onEscape(e); }}
            slotProps={{ input: { ...params.InputProps, endAdornment: (<>{loading ? <CircularProgress color="inherit" size={16} /> : null}{params.InputProps.endAdornment}</>) } }}
          />
        )}
      />
      {/* 選んだ番号の案件名を下に並べる（チップだけでは何の案件か分からないため） */}
      {value.length > 0 && (
        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {value.map((n) => (
            <Typography key={n} variant="caption" color="text.secondary" noWrap>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{formatSystemNumber(n)}</Box>　{infoLine(info[n]) || (info[n]?.in_mirror === false ? '（手入力・ミラー未反映）' : '')}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

// --------------------------------------------------
// 詳細画面のインライン編集（Enter は候補の選択に使うので、保存は ✓ で行う。Esc で取り消し）
// --------------------------------------------------
export function InlineAnkenField({ value, links, onSave, label, disabled }: {
  value: string[] | null | undefined;
  links?: SfaAnkenLink[] | null;
  onSave: (next: string[]) => Promise<void>;
  label?: string;
  disabled?: boolean;
}) {
  const current = normalizeSystemNumbers(value);
  const [info, setInfo] = useState<InfoMap>({});
  return (
    <InlineEdit<string[]>
      value={current} label={label || SYSTEM_NUMBER_LABEL} disabled={disabled} editMinWidth={440}
      display={<AnkenChips numbers={current} links={links} info={info} />}
      isEqual={(a, b) => normalizeSystemNumbers(a).join('\n') === normalizeSystemNumbers(b).join('\n')}
      onSave={(v) => onSave(normalizeSystemNumbers(v))}
      renderEditor={({ draft, setDraft, saving, onKeyDown }) => (
        <AnkenNumberPicker value={draft} links={links} autoFocus disabled={saving} label=""
          onChange={(nums, m) => { setDraft(nums); setInfo(m); }} onEscape={onKeyDown} />
      )}
    />
  );
}
